// ScorelinePage.js — /scorelines/:score, everything about one winner-first
// scoreline: how often it has happened, home v away, season by season, the
// clubs involved, every match, and the scorelines one goal away. Loser-first
// URLs (/scorelines/3-7) redirect; a valid scoreline that has never happened
// gets a short noindex page; anything else is a 404.

import React, { useMemo, useState } from "react";
import { Navigate, useParams, Link as RouterLink } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import NotFoundPage from "./NotFoundPage";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { displayName, formatDate, toWinnerFirst } from "./components/heatmap/utils";
import { clubPath } from "./components/clubs/data";
import {
  bySeason,
  clubTallies,
  countWord,
  fmtScore,
  isDrawKey,
  matchText,
  neighbours,
  numberWord,
  occurrenceText,
  occurrenceWhen,
  parseKey,
  scorelinePath,
  scoreQuery,
  seasonLabel,
  summaryStats,
  useSeededQuery,
  withArticle,
} from "./components/scorelines/data";

const SITE_URL = "https://premierleaguescorigami.com";
const FIRST_ROWS = 25;
const MORE_ROWS = 100;

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const pctText = (p) => (p >= 1 ? p.toFixed(1) : p >= 0.1 ? p.toFixed(2) : p.toFixed(3));

const breadcrumbs = (key) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Scorelines", item: `${SITE_URL}/scorelines` },
    { "@type": "ListItem", position: 3, name: fmtScore(key), item: `${SITE_URL}${scorelinePath(key)}` },
  ],
});

const backLink = <InternalLink to="/scorelines">All scorelines</InternalLink>;

export default function ScorelineRoute() {
  const { score = "" } = useParams();
  if (!/^\d{1,2}-\d{1,2}$/.test(score)) return <NotFoundPage />;
  const key = toWinnerFirst(score);
  if (key !== score) return <Navigate to={scorelinePath(key)} replace />;
  // Keyed so moving between scorelines starts from fresh state.
  return <ScorelinePage key={key} scoreKey={key} />;
}

function ScorelinePage({ scoreKey }) {
  const summary = useSeededQuery("summary=1");
  const list = useSeededQuery(scoreQuery(scoreKey));
  const S = fmtScore(scoreKey);

  const stats = useMemo(() => (summary.data ? summaryStats(summary.data.items || []) : null), [summary.data]);
  const row = stats?.rows.find((r) => r.score === scoreKey);
  const matches = useMemo(
    () => [...(list.data?.items || [])].sort((a, b) => b.utcDate.localeCompare(a.utcDate)),
    [list.data]
  );

  if (summary.error || (row && list.error && !list.data)) {
    return (
      <ProsePage>
        <SEO title={`${S} in the Premier League`} description={`Every ${S} in Premier League history: how often it has happened, home and away, and every match since 1992.`} path={scorelinePath(scoreKey)} />
        <PageHeader eyebrow={backLink} title={`Every ${S} in Premier League history`} />
        <P>This scoreline couldn't be loaded right now. Please try again shortly.</P>
      </ProsePage>
    );
  }
  if (stats && !row) return <NeverHappened scoreKey={scoreKey} stats={stats} />;
  if (!stats || !list.data) return <LoadingPage scoreKey={scoreKey} />;
  return <ScorelineDetails scoreKey={scoreKey} row={row} stats={stats} matches={matches} />;
}

function LoadingPage({ scoreKey }) {
  const S = fmtScore(scoreKey);
  return (
    <ProsePage>
      <SEO title={`Every ${S} in Premier League history`} description={`Every ${S} in Premier League history: how often it has happened, home and away, and every match since 1992.`} path={scorelinePath(scoreKey)} />
      <PageHeader eyebrow={backLink} title={`Every ${S} in Premier League history`} />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
      ))}
    </ProsePage>
  );
}

function ScorelineDetails({ scoreKey, row, stats, matches }) {
  const S = fmtScore(scoreKey);
  const isDraw = isDrawKey(scoreKey);
  const n = row.count;
  const homeN = Number(row.home?.count || 0);
  const awayN = Number(row.away?.count || 0);
  const first = row.firstOccurrence;
  const latest = row.latestOccurrence;
  const { rank, tied } = stats.rankOf(scoreKey);
  const pct = (n / stats.total) * 100;
  const everyN = Math.round(stats.total / n);
  const seasons = useMemo(() => bySeason(matches), [matches]);
  const peak = seasons.reduce((best, s) => (s.count > best.count ? s : best), seasons[0]);
  const clubs = useMemo(() => clubTallies(matches), [matches]);
  const known = new Set(stats.rows.map((r) => r.score));
  const near = neighbours(scoreKey, known).map((k) => stats.rows.find((r) => r.score === k));

  const title = isDraw ? `Every ${S} draw in Premier League history` : `Every ${S} in Premier League history`;
  const rankText =
    n === 1
      ? `one of ${numberWord(stats.oneOffs)} scorelines that have happened only once`
      : rank === 1
        ? "the most common scoreline in Premier League history"
        : `the ${ordinal(rank)}${tied > 1 ? "-equal" : ""} most common of the ${stats.rows.length} scorelines`;
  const lede =
    n === 1
      ? `${S} has happened once in ${stats.total.toLocaleString()} Premier League matches: ${occurrenceText(first)} on ${occurrenceWhen(first)}.`
      : `${S} has happened ${n.toLocaleString()} times in ${stats.total.toLocaleString()} Premier League matches, about once every ${everyN.toLocaleString()} games.` +
        (isDraw ? "" : ` Home sides won ${homeN.toLocaleString()} of them and away sides ${awayN.toLocaleString()}.`);
  const description =
    n === 1
      ? `${S} has happened once in Premier League history: ${occurrenceText(first)}, ${occurrenceWhen(first)}. The match, and the scorelines around it.`
      : `${S} has happened ${n.toLocaleString()} times in the Premier League since 1992${isDraw ? "" : ` (${homeN.toLocaleString()} home wins, ${awayN.toLocaleString()} away)`}. Every match, the clubs involved, and the season-by-season record.`;

  const tiles = [
    { value: n === 1 ? "Once" : n.toLocaleString(), label: "Times" },
    { value: `${pctText(pct)}%`, label: "Of all matches" },
    ...(isDraw
      ? [
          { value: `1 in ${everyN.toLocaleString()}`, label: "Matches" },
          { value: n === 1 ? "One-off" : `#${rank}`, label: "Most common" },
        ]
      : [
          { value: homeN.toLocaleString(), label: "Home wins" },
          { value: awayN.toLocaleString(), label: "Away wins" },
        ]),
  ];

  const everLine =
    n === 1
      ? `Yes, once: ${occurrenceText(first)} on ${occurrenceWhen(first)}. It is ${rankText}.`
      : `Yes, ${countWord(n)}. The first was ${occurrenceText(first)} on ${occurrenceWhen(first)}, and the most recent ${occurrenceText(latest)} on ${occurrenceWhen(latest)}. It is ${rankText}.`;
  let awayLine = null;
  if (!isDraw) {
    if (awayN === 0) {
      awayLine =
        (n === 1 ? `No. The only ${S} was a home win` : `No. All ${n.toLocaleString()} have been home wins`) +
        `, so an away ${S} would be a result the Premier League has never produced: a home–away scorigami.`;
    } else {
      const af = row.away.firstOccurrence;
      const al = row.away.latestOccurrence;
      awayLine =
        `Yes, ${countWord(awayN)}. ` +
        (awayN === 1
          ? `It was ${occurrenceText(af)} on ${occurrenceWhen(af)}.`
          : `The first was ${occurrenceText(af)} on ${occurrenceWhen(af)}; the most recent, ${occurrenceText(al)} on ${occurrenceWhen(al)}.`) +
        (homeN === 0 ? ` No home side has ever won ${S}.` : "");
    }
  }
  const commonLine =
    `${S} accounts for ${pctText(pct)}% of Premier League results, about one match in every ${everyN.toLocaleString()}. That makes it ${rankText}.` +
    (n >= 5 ? ` Its busiest season was ${peak.year}/${String((peak.year + 1) % 100).padStart(2, "0")}, with ${peak.count}.` : "");

  return (
    <ProsePage>
      <SEO title={title} description={description} path={scorelinePath(scoreKey)} jsonLd={breadcrumbs(scoreKey)} />
      <PageHeader eyebrow={backLink} title={title} lede={lede} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.25, mb: 1 }}>
        {tiles.map((t) => (
          <Tile key={t.label} {...t} />
        ))}
      </Box>

      <H2 id="answers">Has there ever been {withArticle(scoreKey)} in the Premier League?</H2>
      <P>{everLine}</P>
      {awayLine && (
        <>
          <H2 id="away">Has a team ever won {S} away from home?</H2>
          <P>{awayLine}</P>
        </>
      )}
      <H2 id="how-common">How common is {withArticle(scoreKey)}?</H2>
      <P>{commonLine}</P>

      {n >= 5 && (
        <>
          <H2 id="by-season">{S} season by season</H2>
          <SeasonChart data={seasons} />
        </>
      )}

      <AdSlot position="article" />

      {n >= 4 && (
        <>
          <H2 id="clubs">{isDraw ? `Clubs with the most ${S} draws` : `The clubs behind ${S}`}</H2>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: isDraw ? "1fr" : "1fr 1fr" }, gap: { xs: 2, sm: 4 }, mb: 2 }}>
            <ClubList title={isDraw ? `Drew ${S}` : `Won ${S}`} rows={clubs.won} />
            {!isDraw && <ClubList title={`Lost ${S}`} rows={clubs.lost} />}
          </Box>
        </>
      )}

      <H2 id="matches">{n === 1 ? "The match" : `Every ${S}, newest first`}</H2>
      <MatchList matches={matches} />
      {n === 1 && (
        <P>
          The stories behind this and the Premier League's other one-off scorelines are in{" "}
          <InternalLink to="/articles/rarest-premier-league-scorelines">the rarest scorelines in Premier League history</InternalLink>.
        </P>
      )}

      {near.length > 0 && (
        <>
          <H2 id="nearby">One goal away</H2>
          <P>Scorelines one goal either side of {S}, and how often each has happened.</P>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {near.map((r) => (
              <Box
                key={r.score}
                component={RouterLink}
                to={scorelinePath(r.score)}
                sx={{
                  px: 1.75,
                  py: 0.9,
                  borderRadius: 999,
                  textDecoration: "none",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14.5,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.04)",
                  "&:hover": { borderColor: BRAND.cyan },
                }}
              >
                {fmtScore(r.score)}
                <Box component="span" sx={{ color: BRAND.textSecondary, fontWeight: 500, ml: 1 }}>
                  {countWord(r.count)}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      <CtaRow
        links={[
          { to: "/", label: "Explore the grid", primary: true },
          { to: "/scorelines", label: "All scorelines" },
          { to: "/records", label: "Records" },
        ]}
      />
    </ProsePage>
  );
}

function NeverHappened({ scoreKey, stats }) {
  const S = fmtScore(scoreKey);
  const [hi] = parseKey(scoreKey);
  const maxGoals = Math.max(...stats.rows.map((r) => parseKey(r.score)[0]));
  const known = new Set(stats.rows.map((r) => r.score));
  const near = neighbours(scoreKey, known);
  return (
    <ProsePage>
      <SEO
        title={`${S} has never happened in the Premier League`}
        description={`No Premier League match has ever finished ${S}.`}
        path={scorelinePath(scoreKey)}
        noindex
      />
      <PageHeader
        eyebrow={backLink}
        title={`No Premier League match has ever finished ${S}`}
        lede={`In ${stats.total.toLocaleString()} matches since August 1992, ${S} has never happened. If it ever does, it will be a scorigami, and @pl_gami will post it at full time.`}
      />
      {hi > maxGoals && <P>No team has ever scored {hi} goals in a Premier League match. The most is {maxGoals}.</P>}
      {near.length > 0 && (
        <P>
          The nearest scorelines that have happened:{" "}
          {near.map((k, i) => (
            <React.Fragment key={k}>
              {i > 0 && ", "}
              <InternalLink to={scorelinePath(k)}>{fmtScore(k)}</InternalLink>
            </React.Fragment>
          ))}
          .
        </P>
      )}
      <CtaRow
        links={[
          { to: "/scorelines", label: "All scorelines", primary: true },
          { to: "/", label: "Explore the grid" },
        ]}
      />
    </ProsePage>
  );
}

function Tile({ value, label }) {
  return (
    <Box sx={{ px: 1.75, py: 1.5, borderRadius: 2, background: "rgba(255,255,255,0.035)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <Typography
        sx={{
          fontSize: { xs: 22, sm: 26 },
          fontWeight: 900,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          fontFeatureSettings: '"tnum"',
          background: BRAND.gradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", mt: 0.6 }}>
        {label}
      </Typography>
    </Box>
  );
}

function SeasonChart({ data }) {
  return (
    <Box sx={{ height: 200, mb: 2, mx: -1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={2}>
          <defs>
            <linearGradient id="seasonGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BRAND.cyan} stopOpacity={0.95} />
              <stop offset="100%" stopColor={BRAND.violet} stopOpacity={0.75} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: BRAND.textSecondary }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
          <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11, fill: BRAND.textSecondary }} tickLine={false} axisLine={false} />
          <Tooltip
            cursor={{ fill: "rgba(111,93,245,0.1)" }}
            contentStyle={{ background: "rgba(15,15,19,0.95)", border: `1px solid ${BRAND.divider}`, borderRadius: 8, fontSize: 12.5 }}
            labelStyle={{ color: BRAND.cyan, fontWeight: 700 }}
            itemStyle={{ color: "#fff" }}
            formatter={(v) => [`${v} match${v === 1 ? "" : "es"}`, ""]}
            labelFormatter={(l) => `Season ${l}`}
            separator=""
          />
          <Bar dataKey="count" fill="url(#seasonGrad)" radius={[3, 3, 0, 0]} maxBarSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function ClubList({ title, rows }) {
  const top = rows.slice(0, 8);
  return (
    <Box>
      <Typography sx={{ color: BRAND.cyan, fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", mb: 1 }}>
        {title}
      </Typography>
      {top.map(([club, count]) => (
        <Box
          key={club}
          sx={{ display: "flex", justifyContent: "space-between", py: 0.9, borderBottom: "1px solid rgba(255,255,255,0.06)", fontFeatureSettings: '"tnum"' }}
        >
          <Typography component={RouterLink} to={clubPath(club)} sx={{ color: "#fff", fontWeight: 600, fontSize: 15, textDecoration: "none", "&:hover": { color: BRAND.cyan } }}>
            {displayName(club)}
          </Typography>
          <Typography sx={{ color: BRAND.textSecondary, fontSize: 14.5 }}>{countWord(count)}</Typography>
        </Box>
      ))}
      {rows.length > top.length && (
        <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, mt: 1 }}>
          and {rows.length - top.length} other club{rows.length - top.length === 1 ? "" : "s"}
        </Typography>
      )}
    </Box>
  );
}

// Plain rows styled from the parent, so a list of 2,000+ matches doesn't
// create a style per row.
function MatchList({ matches }) {
  const [shown, setShown] = useState(FIRST_ROWS);
  const rest = matches.length - shown;
  return (
    <Box
      sx={{
        mb: 2,
        "& .m-row": {
          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", sm: "150px 1fr auto" },
          alignItems: "center",
          columnGap: 2,
          py: 1.1,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        },
        "& .m-date": { color: BRAND.textSecondary, fontSize: 13, fontFeatureSettings: '"tnum"', gridColumn: { xs: "1 / 2", sm: "auto" } },
        "& .m-text": { color: "#fff", fontSize: 15, fontWeight: 700, gridColumn: { xs: "1 / 2", sm: "auto" }, gridRow: { xs: 2, sm: "auto" } },
        "& .m-season": {
          gridRow: { xs: "1 / 3", sm: "auto" },
          gridColumn: { xs: 2, sm: "auto" },
          color: BRAND.textSecondary,
          fontSize: 12.5,
          fontWeight: 700,
          fontFeatureSettings: '"tnum"',
        },
      }}
    >
      {matches.slice(0, shown).map((m) => (
        <div className="m-row" key={m.matchId || `${m.utcDate}-${m.homeTeam}`}>
          <span className="m-date">{formatDate(m.utcDate)}</span>
          <span className="m-text">{matchText(m)}</span>
          <span className="m-season">{seasonLabel(m.utcDate)}</span>
        </div>
      ))}
      {rest > 0 && (
        <Button
          onClick={() => setShown((s) => s + MORE_ROWS)}
          variant="outlined"
          sx={{ mt: 2, borderRadius: 999, textTransform: "none", fontWeight: 700, color: "#e6e8ee", borderColor: "rgba(255,255,255,0.18)", "&:hover": { borderColor: BRAND.cyan } }}
        >
          Show {Math.min(rest, MORE_ROWS)} more of {rest.toLocaleString()}
        </Button>
      )}
    </Box>
  );
}
